import { initReactI18next } from 'react-i18next'

import i18n from 'i18next'
import i18nBackend from 'i18next-http-backend'

import countryCodeMapJson from './assets/json/country-code-map.json'
import languageCodeMapJson from './assets/json/language-code-map.json'
import languageLocalNameListJson from './assets/json/language-local-name-list.json'
import supportedLanguageListJson from './assets/json/supported-language-list.json'
import { languageDetector } from './language-detector'

declare module 'i18next' {
  interface i18n {
    languageCode: string
    languageCode3digit: string
    countryCode: string
    countryCode3digit: string
    isRtl: boolean
  }
}

type SupportedLanguages = {
  group: string
  countryList: {
    countryName: string
    countryCode: string
    languages: {
      language: string
      locale: string
    }[]
  }[]
}[]

type CodeMap = {
  name: string
  digit2: string
  digit3: string
}[]

type LanguageLocalNames = {
  languageCode: string
  languageLocalName: string
}[]

export const supportedLanguageList: SupportedLanguages = supportedLanguageListJson
const countryCodeMap: CodeMap = countryCodeMapJson
const languageCodeMap: CodeMap = languageCodeMapJson
export const languageLocalNameList: LanguageLocalNames = languageLocalNameListJson

export const getFullNameByLocale = (locale: string): string => {
  let result = ''
  let findResult

  supportedLanguageList.forEach((group) => {
    group.countryList.forEach((country) => {
      findResult = country.languages.find((language) => language.locale === locale)
      if (findResult !== undefined) {
        result = `${country.countryName} / ${findResult.language}`
        return false
      }
    })
    if (findResult !== undefined) {
      return false
    }
  })
  return result
}

export const getMultipleLanguageByCountry = (
  _countryCode: string
): { language: string; locale: string; country: string; fullName: string; isRtl: boolean }[] => {
  let result: { language: string; locale: string; country: string; fullName: string; isRtl: boolean }[] = []
  supportedLanguageList.forEach((group) => {
    let findResult = group.countryList.find((item) => item.countryCode === _countryCode)
    if (findResult !== undefined) {
      result = findResult.languages.map((lang) => {
        const fullName = getFullNameByLocale(lang.locale)
        return {
          ...lang,
          fullName,
          country: fullName.split(' / ')[0],
          isRtl: i18n.dir(lang.locale) === 'rtl'
        }
      })
    }
  })

  return result
}

export const getLangugageByCountry = (_countryCode: string): string | null => {
  let result = null
  let findResult
  supportedLanguageList.forEach((group) => {
    findResult = group.countryList.find((item) => item.countryCode === _countryCode)
    if (findResult !== undefined) {
      result = findResult.language[0].locale.split('-')[0]
      return result
    }
  })

  return result
}

const convertCountryCodeTo2Digit = (countryCode: string): string | undefined => {
  const normalizedCountryCode = countryCode.trim()

  if (normalizedCountryCode.length === 2) {
    return normalizedCountryCode.toUpperCase()
  }

  const target = countryCodeMap.find((country) => country.digit3.toLowerCase() === normalizedCountryCode.toLowerCase())
  return target?.digit2.toUpperCase()
}

export const getDefaultLocaleForCountry = (_countryCode: string): string | undefined => {
  const countryCode2Digit = convertCountryCodeTo2Digit(_countryCode)

  if (!countryCode2Digit) {
    return undefined
  }

  for (const group of supportedLanguageList) {
    const country = group.countryList.find((c) => c.countryCode.toUpperCase() === countryCode2Digit)
    if (country && country.languages.length > 0) {
      return country.languages[0].locale
    }
  }
  return undefined
}

export const getGroupIndexByLocale = (locale: string): number => {
  let result = -1
  const findTarget = locale.split('-').at(1)

  if (findTarget === undefined) return -1

  supportedLanguageList.forEach((group, index) => {
    const findResult = group.countryList.find((country) => country.countryCode === findTarget)

    if (findResult !== undefined) {
      result = index
      return false
    }
  })

  return result
}

const fallbackCountryCode3digits = 'USA'
export const convertCountryCode2to3 = (countryCode: string): string => {
  const normalizedCountryCode = countryCode.trim()

  if (normalizedCountryCode.length === 3) {
    return normalizedCountryCode.toUpperCase()
  }

  const target = countryCodeMap.find((country) => country.digit2.toLowerCase() === normalizedCountryCode.toLowerCase())

  if (target === undefined) {
    return fallbackCountryCode3digits
  }

  return target.digit3.toUpperCase()
}

export const convertLanguageCode2to3 = (languageCode: string): string => {
  const normalizedLanguageCode = languageCode.trim().toLowerCase()
  const target = languageCodeMap.find((language) => language.digit2.toLowerCase() === normalizedLanguageCode)

  if (target === undefined) {
    return ''
  }
  return target.digit3
}

export const convertCountryCode3to2 = (countryCode: string): string => {
  if (countryCode.length === 2) {
    return countryCode
  }

  const target = countryCodeMap.find((country) => country.digit3.toLowerCase() === countryCode.toLowerCase())

  if (target === undefined) {
    throw new Error('failed to convert ' + countryCode)
  }

  return target.digit2
}

export const convertLanguageCode3to2 = (languageCode: string): string => {
  if (languageCode.length === 2) {
    return languageCode
  }

  const target = languageCodeMap.find((language) => language.digit3.toLowerCase() === languageCode.toLowerCase())

  if (target === undefined) {
    throw new Error('failed to convert ' + languageCode)
  }

  return target.digit2
}

export const getCountryNameFromCountryCode = (countryCode: string): string => {
  const code = convertCountryCode2to3(countryCode)

  return `membership.00.Common.CountryName.${code}`
}

export let languageCode
export let countryCode
export let isRtl
export const isChinaRegion = window.location.hostname.endsWith('.cn')

const familyNameOrderFirstCountries = ['ko', 'zh', 'ja', 'ar', 'th', 'vi', 'hu']
export const isFamilyNameOrderFirst = () => {
  if (!i18n.resolvedLanguage) {
    return false
  }
  const language = i18n.resolvedLanguage.split('-')[0]
  return familyNameOrderFirstCountries.includes(language)
}

const REGEX_CJK = new RegExp(
  [
    '[\uac00-\ud7a3\u1100-\u11ff\u3130-\u318f\u30a0-\u30ff',
    '\u3040-\u309f\u31f0-\u31ff\u4e00-\u9fef\u3400-\u4dbf\u3000-\u303f',
    '\u2e80-\u2eff\u3300-\u33ff\ufe30-\ufe4f\uf900-\ufaff]',
    '|[\ud840-\ud868]'
  ].join('')
)
export const isCJK = (name: string): boolean => {
  let hasCJKChar: boolean = false
  let hasOtherChar: boolean = false

  for (const ch of name) {
    if (REGEX_CJK.test(ch)) {
      hasCJKChar = true
    } else {
      hasOtherChar = true
    }
  }
  return hasCJKChar && !hasOtherChar
}

//TODO: Apply geoip & fix region issue
i18n.on('languageChanged', (lng) => {
  const language = lng.split('-')[0]
  const region = lng.split('-')[1] || 'US'

  //deprecated normal languageCode, countryCode, isRtl
  languageCode = convertLanguageCode2to3(language)
  countryCode = convertCountryCode2to3(region)
  isRtl = i18n.dir() === 'rtl'

  i18n.languageCode = language
  i18n.countryCode = region
  i18n.languageCode3digit = convertLanguageCode2to3(language)?.toUpperCase()
  i18n.countryCode3digit = convertCountryCode2to3(region)?.toUpperCase()
  i18n.isRtl = i18n.dir() === 'rtl'

  document.body.dir = i18n.dir()
  document.title = i18n.t('iam.01.Common.Logo')
})

i18n
  .use(initReactI18next)
  .use(languageDetector)
  .use(i18nBackend)
  .use({
    type: 'postProcessor',
    name: 'postProcessor',
    process: function (value) {
      return value.replaceAll('<br>', '<br/>')
    }
  })
  .init({
    backend: {
      loadPath: '/assets/locales/{{lng}}.json',
      allowMultiLoading: true
    },
    ns: ['common'],
    defaultNS: 'common',
    fallbackLng: 'en',
    debug: false,
    keySeparator: false,
    interpolation: {
      escapeValue: false
    },
    postProcess: ['postProcessor']
  })

export default i18n
